import { Breakpoints, CTA, Layout, Text, ThemeProvider, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import { ChevronRight } from '@volkswagen-onehub/icons-core';
import React, { ReactElement, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { TextInputField } from '../forms/fields/TextInputField';
import { RadioButtonField } from '../forms/fields/RadioButtonField';
import { PersonalIdentityType } from './PersonalIdentityType';
import { ContactMeUrl } from '../navigation/ExternalLinks';

// The VW "Book your service" page that finds the customer's vehicle: login by registration number
// and login by VIN share it and differ only in the intro and the vehicle field of step 1.

// The theme handed to the feature app has no theme.color / theme.font, so colours are taken from the VW palette directly.
const VwDarkBlue = '#001e50';

// The design sets all copy on these pages in VW dark blue instead of the default black.
const darkBlueText = { colors: { content: { primary: VwDarkBlue } } };

const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 610px;
  color: ${VwDarkBlue};

  input::placeholder {
    color: #96a3a8;
    opacity: 1;
  }
`;

const Headline = styled(Text)`
  margin-bottom: ${(props) => props.theme.size.static250};
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.size.static150};
  margin-bottom: ${(props) => props.theme.size.static350};
`;

// The design draws the inputs as white boxes rather than the VW underline. The step heading names
// each field on screen, so the input's own label is kept for screen readers only.
const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.size.static400};

  label > p {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  /* VW draws its hover and focus line as a box-shadow on the input, which the box border replaces. */
  label > div input {
    box-sizing: border-box;
    height: 46px;
    padding: 0 ${(props) => props.theme.size.static200} !important;
    border: 1px solid #c2cacf;
    border-radius: 4px;
    background: #ffffff;
    box-shadow: none !important;
  }

  label > div input:hover {
    border-color: #6a767d;
  }

  label > div input:focus {
    border-color: ${VwDarkBlue};
  }

  label > div input[aria-invalid='true'] {
    border-color: #e4002c;
    padding-right: 48px !important;
  }

  /* VW pins its error icon to the bottom corner, under where its underline would be. */
  label > div > input ~ div {
    position: absolute;
    top: 50%;
    bottom: auto;
    right: ${(props) => props.theme.size.static200};
    transform: translateY(-50%);
  }
`;

const StepRow = styled.div`
  display: flex;
  gap: ${(props) => props.theme.size.static300};
`;

const StepNumber = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${VwDarkBlue};
  color: #ffffff;
  font-size: 20px;
  font-weight: bold;

  @media (min-width: ${Breakpoints.b560}px) {
    width: 48px;
    height: 48px;
  }
`;

const StepBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.size.static200};
  padding-top: ${(props) => props.theme.size.static150};
`;

const StepTitle = styled.div`
  font-weight: bold;
`;

const SecureNote = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.size.static150};
`;

// Narrower than the fields, so the note breaks after "your customer" as in the design.
const SecureNoteText = styled.div`
  max-width: 440px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${(props) => props.theme.size.static500};
  row-gap: ${(props) => props.theme.size.static200};

  & > label {
    flex: 0 0 auto;
    width: auto;
  }
`;

const SubmitContainer = styled.div`
  margin-top: ${(props) => props.theme.size.static400};
`;

const ContactBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.size.static300};
  margin-top: ${(props) => props.theme.size.static400};
  padding: ${(props) => props.theme.size.static300};
  border-radius: 4px;
  background: #e5edfa;
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${(props) => props.theme.size.static300};
`;

const ContactIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-right: ${(props) => props.theme.size.static300};
  border-right: 1px solid #c2cacf;
`;

const ContactText = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 440px;
  gap: ${(props) => props.theme.size.static100};
`;

// VW icons have no filled padlock, which is what the design shows next to the ID note.
function FilledLock(): ReactElement {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M7 10.5V7a5 5 0 0 1 10 0v3.5" fill="none" stroke="currentColor" strokeWidth="2.6" />
      <rect x="3.5" y="10" width="17" height="13" rx="2" />
      <path d="M12 13.6a1.7 1.7 0 0 0-1 3.07l-.45 3.13h2.9l-.45-3.13a1.7 1.7 0 0 0-1-3.07z" fill="#ffffff" />
    </svg>
  );
}

// No headset in VW icons either; drawn after the one in the design.
function Headset(): ReactElement {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M5 13v-2a7 7 0 0 1 14 0v2" fill="none" stroke="currentColor" strokeWidth="1.9" />
      <rect x="3" y="11.5" width="4" height="7" rx="2" />
      <rect x="17" y="11.5" width="4" height="7" rx="2" />
      <path d="M19 18.5c0 1.9-1.9 3-4.6 3H13" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="12" cy="21.5" r="1.7" />
    </svg>
  );
}

export function VehicleLookupStep({ number, title, children }: { number: number; title: string; children: ReactNode }): ReactElement {
  return (
    <StepRow>
      <StepNumber aria-hidden="true">{number}</StepNumber>
      <StepBody>
        <StepTitle>{title}</StepTitle>
        {children}
      </StepBody>
    </StepRow>
  );
}

export function VehicleLookupIdentityStep(): ReactElement {
  const { watch } = useFormContext();
  const identificationType: PersonalIdentityType = watch('identity.identityType');

  return (
    <VehicleLookupStep number={2} title="Your ID or passport number">
      <SecureNote>
        <FilledLock />
        <SecureNoteText>
          <Text appearance={TokenTextAppearance.copy100}>
            Your ID or passport number is used securely to identify your customer and vehicle information.
          </Text>
        </SecureNoteText>
      </SecureNote>
      <RadioGroup role="radiogroup" aria-label="Identification type">
        <RadioButtonField
          name="identity.identityType"
          value={PersonalIdentityType.CustomerId}
          label="South African ID number"
        />
        <RadioButtonField
          name="identity.identityType"
          value={PersonalIdentityType.PassportNumber}
          label="Passport number"
        />
      </RadioGroup>
      {identificationType === PersonalIdentityType.CustomerId && (
        <TextInputField
          key="customerId"
          name="identity.customerId"
          label="South African ID number"
          type="text"
          rules={{ min: 13, max: 13 }}
          trimString
          errorBelow
        />
      )}
      {identificationType === PersonalIdentityType.PassportNumber && (
        <TextInputField
          key="passportNumber"
          name="identity.passportNumber"
          label="Passport number"
          type="text"
          rules={{ max: 12 }}
          trimString
          errorBelow
        />
      )}
    </VehicleLookupStep>
  );
}

export function VehicleLookupSteps({ children }: { children: ReactNode }): ReactElement {
  return <StepsList>{children}</StepsList>;
}

export function VehicleLookupSubmit(): ReactElement {
  return (
    <SubmitContainer>
      <CTA type="submit" tag="button" icon={<ChevronRight />} iconPosition="end" stretchContent>
        <strong>Submit to find your vehicle</strong>
      </CTA>
    </SubmitContainer>
  );
}

interface VehicleLookupVwLayoutProps {
  intro: string;
  // The Contact Me box for customers who would rather not give their ID online.
  withContactMe?: boolean;
  children: ReactNode;
}

// The form goes in as children so each page keeps its own schema and form data types.
export function VehicleLookupVwLayout({ intro, withContactMe = false, children }: VehicleLookupVwLayoutProps): ReactElement {
  return (
    <Layout allowOverflowingContent>
      <ThemeProvider theme={darkBlueText}>
        <Content>
          <Headline appearance={TokenTextAppearance.headline400}>
            Book <Text bold>your service</Text>
          </Headline>
          <Intro>
            <Text appearance={TokenTextAppearance.headline200} bold>Let’s find your vehicle</Text>
            <Text appearance={TokenTextAppearance.copy200}>{intro}</Text>
          </Intro>
          {children}
          {withContactMe && (
            <ContactBox>
              <ContactInfo>
                <ContactIcon>
                  <Headset />
                </ContactIcon>
                <ContactText>
                  <Text appearance={TokenTextAppearance.copy200} bold>Prefer not to provide your ID online?</Text>
                  <Text appearance={TokenTextAppearance.copy100}>
                    Choose Contact Me and one of our Customer Interaction Centre Agents will call you back to make a booking.
                  </Text>
                </ContactText>
              </ContactInfo>
              <CTA tag="a" href={ContactMeUrl} emphasis="secondary" stretchContent>
                <strong>Contact Me</strong>
              </CTA>
            </ContactBox>
          )}
        </Content>
      </ThemeProvider>
    </Layout>
  );
}
